import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Upload, FolderPlus, Receipt, Filter, Search, Loader2,
  LayoutGrid, List, Trash2, Download, Tag, FolderInput
} from 'lucide-react';
import { FileList } from './FileList';
import { FilePreviewPanel } from './FilePreviewPanel';
import { FileUploadZone } from './FileUploadZone';
import { FileFiltersDrawer } from './FileFiltersDrawer';
import { RequestReceiptModal } from './RequestReceiptModal';
import { QUICK_FILTERS } from './fileConfig';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FilesTab = ({ jobId, currentUserId }) => {
  const navigate = useNavigate();
  
  // State
  const [files, setFiles] = useState([]);
  const [signedEstimates, setSignedEstimates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState('list');

  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    categories: [],
    tags: [],
    visibility: null,
    uploaded_by: null,
    date_from: null,
    date_to: null,
    file_type: null
  });

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [fileForVersion, setFileForVersion] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Upload form state
  const [uploadSettings, setUploadSettings] = useState({
    categoryId: '',
    visibility: 'internal',
    tags: ''
  });

  // Fetch files
  const fetchFiles = useCallback(async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (searchQuery) params.append('search', searchQuery);
      if (advancedFilters.visibility) params.append('visibility', advancedFilters.visibility);
      if (advancedFilters.uploaded_by) params.append('uploaded_by', advancedFilters.uploaded_by);
      if (advancedFilters.file_type) params.append('file_type', advancedFilters.file_type);

      const response = await axios.get(`${API}/jobs/${jobId}/files?${params.toString()}`);
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [jobId, searchQuery, advancedFilters]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/file-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/files/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, [jobId]);

  // Fetch signed estimates
  const fetchSignedEstimates = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/estimates`);
      const signed = (response.data || []).filter(e => 
        e.status === 'SIGNED' || e.status === 'ACCEPTED'
      );
      setSignedEstimates(signed);
    } catch (error) {
      console.error('Error fetching signed estimates:', error);
    }
  }, [jobId]);

  useEffect(() => {
    fetchFiles();
    fetchCategories();
    fetchUsers();
    fetchTags();
    fetchSignedEstimates();
  }, [fetchFiles, fetchCategories, fetchUsers, fetchTags, fetchSignedEstimates]);

  // Convert signed estimates to file-like objects for display
  const signedEstimateFiles = signedEstimates.map(est => ({
    id: est.pdf_file_id || `estimate-${est.id}`,
    file_name: `${est.title || 'Estimate'} - Signed Agreement.pdf`,
    file_type: 'application/pdf',
    file_url: est.pdf_file_url,  // Actual PDF URL if generated
    category_name: 'Agreements / Contracts',
    visibility: 'customer',
    uploaded_at: est.signed_at || est.updated_at || est.created_at,
    file_size: null,
    is_signed_estimate: true,
    estimate_id: est.id,
    estimate: est,
    can_preview: !!est.pdf_file_url,  // Can preview if PDF was generated
    can_download: !!est.pdf_file_url   // Can download if PDF was generated
  }));

  // Filter files locally based on quick filter
  const filteredFiles = (() => {
    // If signed agreements filter is active, only show signed estimates
    if (activeQuickFilter === 'signed-agreements') {
      return signedEstimateFiles;
    }

    // Otherwise filter regular files
    let filtered = files.filter(file => {
      // Quick filter
      if (activeQuickFilter !== 'all') {
        const filter = QUICK_FILTERS.find(f => f.id === activeQuickFilter);
        if (filter) {
          if (filter.category && file.category_name !== filter.category) return false;
          if (filter.categories && !filter.categories.includes(file.category_name)) return false;
          if (filter.visibility && file.visibility !== filter.visibility) return false;
        }
      }

      // Advanced category filter
      if (advancedFilters.categories?.length > 0) {
        if (!advancedFilters.categories.includes(file.category_id)) return false;
      }

      // Advanced tag filter
      if (advancedFilters.tags?.length > 0) {
        if (!advancedFilters.tags.some(t => file.tags?.includes(t))) return false;
      }

      // Date filters
      if (advancedFilters.date_from) {
        if (new Date(file.uploaded_at) < new Date(advancedFilters.date_from)) return false;
      }
      if (advancedFilters.date_to) {
        if (new Date(file.uploaded_at) > new Date(advancedFilters.date_to)) return false;
      }

      return true;
    });

    // For agreements filter, include signed estimates
    if (activeQuickFilter === 'agreements') {
      filtered = [...signedEstimateFiles, ...filtered];
    }

    return filtered;
  })();

  // Count active filters
  const activeFiltersCount = [
    (advancedFilters.categories || []).length,
    (advancedFilters.tags || []).length,
    advancedFilters.visibility ? 1 : 0,
    advancedFilters.uploaded_by ? 1 : 0,
    advancedFilters.date_from ? 1 : 0,
    advancedFilters.date_to ? 1 : 0,
    advancedFilters.file_type ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  // Upload file
  const handleUpload = async (file, settings = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category_id', settings.categoryId || uploadSettings.categoryId || '');
    formData.append('visibility', settings.visibility || uploadSettings.visibility || 'internal');
    formData.append('tags', settings.tags || uploadSettings.tags || '');

    try {
      const response = await axios.post(`${API}/jobs/${jobId}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFiles(prev => [response.data, ...prev]);
      toast.success('File uploaded successfully');
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
      throw error;
    }
  };

  // Update file
  const handleUpdateFile = async (fileId, updates) => {
    try {
      const response = await axios.patch(`${API}/files/${fileId}`, updates);
      setFiles(prev => prev.map(f => f.id === fileId ? response.data : f));
      if (selectedFile?.id === fileId) {
        setSelectedFile(response.data);
      }
      toast.success('File updated');
    } catch (error) {
      console.error('Error updating file:', error);
      toast.error('Failed to update file');
    }
  };

  // Delete file
  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Are you sure you want to delete "${file.file_name}"?`)) return;

    try {
      await axios.delete(`${API}/files/${file.id}`);
      setFiles(prev => prev.filter(f => f.id !== file.id));
      if (selectedFile?.id === file.id) {
        setSelectedFile(null);
      }
      toast.success('File deleted');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  // Download file
  const handleDownload = (file) => {
    if (file.download_url) {
      window.open(file.download_url, '_blank');
    }
  };

  // Upload new version
  const handleUploadVersion = async (file, newFile, notes = '') => {
    const formData = new FormData();
    formData.append('file', newFile);
    formData.append('notes', notes);

    try {
      const response = await axios.post(`${API}/files/${file.id}/version`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFiles(prev => prev.map(f => f.id === file.id ? response.data : f));
      if (selectedFile?.id === file.id) {
        setSelectedFile(response.data);
      }
      toast.success('New version uploaded');
      setShowVersionModal(false);
      setFileForVersion(null);
    } catch (error) {
      console.error('Error uploading version:', error);
      toast.error('Failed to upload new version');
    }
  };

  // Handle file actions from list
  const handleFileAction = async (action, file) => {
    // Handle signed estimate clicks
    if (file.is_signed_estimate) {
      if (action === 'preview') {
        // If we have a PDF URL, get a signed URL and open it
        if (file.file_url || file.storage_key) {
          try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('token');
            const response = await axios.post(`${API}/files/get-signed-url`, {
              file_id: file.id,
              storage_key: file.storage_key
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const signedUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.signed_url}`;
            window.open(signedUrl, '_blank');
            return;
          } catch (error) {
            console.error('Error getting signed URL:', error);
            toast.error('Failed to open file');
            return;
          }
        }
        // Otherwise navigate to present mode for signed estimate
        navigate(`/workspace/jobs/${jobId}/estimates/${file.estimate_id}/present?signed=true`);
        return;
      }
      if (action === 'download') {
        // If we have a storage key, get a signed URL for download
        if (file.file_url || file.storage_key) {
          try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('token');
            const response = await axios.post(`${API}/files/get-signed-url`, {
              file_id: file.id,
              storage_key: file.storage_key
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const signedUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.signed_url}`;
            const link = document.createElement('a');
            link.href = signedUrl;
            link.download = file.file_name || 'Signed_Estimate.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
          } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Failed to download file');
            return;
          }
        }
        // Otherwise show message
        toast.info('PDF is being generated. Please try again in a moment.');
        return;
      }
    }

    switch (action) {
      case 'preview':
        setSelectedFile(file);
        break;
      case 'download':
        handleDownload(file);
        break;
      case 'delete':
        handleDeleteFile(file);
        break;
      case 'visibility':
        await handleUpdateFile(file.id, {
          visibility: file.visibility === 'customer' ? 'internal' : 'customer'
        });
        break;
      case 'version':
        setFileForVersion(file);
        setShowVersionModal(true);
        break;
      case 'rename':
        const newName = window.prompt('Enter new file name:', file.file_name);
        if (newName && newName !== file.file_name) {
          await handleUpdateFile(file.id, { file_name: newName });
        }
        break;
      case 'category':
        // For simplicity, just select the file to edit in panel
        setSelectedFile(file);
        break;
      case 'tag':
        setSelectedFile(file);
        break;
      case 'link':
        toast.info('Link to entity feature coming soon');
        break;
      default:
        break;
    }
  };

  // Handle selection toggle
  const handleSelectToggle = (fileId) => {
    setSelectedIds(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Request receipt
  const handleRequestReceipt = async (data) => {
    try {
      setIsUploading(true);
      const response = await axios.post(`${API}/jobs/${jobId}/files/request-receipt`, data);
      toast.success(`Receipt request sent to ${response.data.notifications_sent} recipient(s)`);
      if (response.data.task_created) {
        toast.success('Task created successfully');
      }
      setShowReceiptModal(false);
    } catch (error) {
      console.error('Error requesting receipt:', error);
      toast.error('Failed to send receipt request');
    } finally {
      setIsUploading(false);
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setAdvancedFilters({
      categories: [],
      tags: [],
      visibility: null,
      uploaded_by: null,
      date_from: null,
      date_to: null,
      file_type: null
    });
    setActiveQuickFilter('all');
    setSearchQuery('');
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} files?`)) return;

    for (const id of selectedIds) {
      try {
        await axios.delete(`${API}/files/${id}`);
      } catch (error) {
        console.error(`Error deleting file ${id}:`, error);
      }
    }

    setFiles(prev => prev.filter(f => !selectedIds.includes(f.id)));
    setSelectedIds([]);
    toast.success(`Deleted ${selectedIds.length} files`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-280px)] min-h-[500px]">
      {/* Main Content - File List */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 p-4 border-b bg-background/95 backdrop-blur">
          {/* Top Row - Title & Actions */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Files</h2>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowUploadModal(true)} size="sm" className="gap-1" data-onboard="upload-file-btn">
                <Upload className="h-4 w-4" />
                Upload
              </Button>
              <Button variant="outline" size="sm" className="gap-1">
                <FolderPlus className="h-4 w-4" />
                New Folder
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="gap-1"
                onClick={() => setShowReceiptModal(true)}
              >
                <Receipt className="h-4 w-4" />
                Request Receipt
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-1"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete ({selectedIds.length})
                </Button>
              )}
            </div>
          </div>

          {/* Search & Filters Row */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowFiltersDrawer(true)}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge variant="secondary\" className="ml-1">{activeFiltersCount}</Badge>
              )}
            </Button>
            <div className="flex items-center rounded-lg border p-0.5 ml-auto">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Quick Filter Chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {QUICK_FILTERS.map(filter => (
              <Badge
                key={filter.id}
                variant={activeQuickFilter === filter.id ? 'default' : 'outline'}
                className="cursor-pointer whitespace-nowrap"
                onClick={() => setActiveQuickFilter(filter.id)}
              >
                {filter.label}
                {activeQuickFilter === filter.id && filter.id !== 'all' && (
                  <span className="ml-1">
                    ({filteredFiles.length})
                  </span>
                )}
              </Badge>
            ))}
          </div>
        </div>

        {/* File List */}
        <FileList
          files={filteredFiles}
          selectedIds={selectedIds}
          onSelect={handleSelectToggle}
          onFileClick={setSelectedFile}
          onFileAction={handleFileAction}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </div>

      {/* Preview Panel */}
      <div className="w-[380px] border-l hidden lg:block">
        <FilePreviewPanel
          file={selectedFile}
          categories={categories}
          onClose={() => setSelectedFile(null)}
          onUpdate={handleUpdateFile}
          onDelete={handleDeleteFile}
          onDownload={handleDownload}
          onUploadVersion={(file) => {
            setFileForVersion(file);
            setShowVersionModal(true);
          }}
        />
      </div>

      {/* Filters Drawer */}
      <FileFiltersDrawer
        open={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        onApply={() => {
          setShowFiltersDrawer(false);
          fetchFiles();
        }}
        onClear={handleClearFilters}
        categories={categories}
        users={users}
        tags={tags}
      />

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
            <DialogDescription>
              Drag and drop files or click to browse. Set category and visibility.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <FileUploadZone
              onUpload={handleUpload}
              categories={categories}
              isUploading={isUploading}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={uploadSettings.categoryId || 'none'}
                  onValueChange={(val) => setUploadSettings(prev => ({
                    ...prev,
                    categoryId: val === 'none' ? '' : val
                  }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select
                  value={uploadSettings.visibility}
                  onValueChange={(val) => setUploadSettings(prev => ({ ...prev, visibility: val }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal Only</SelectItem>
                    <SelectItem value="customer">Customer Visible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={uploadSettings.tags}
                onChange={(e) => setUploadSettings(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="Enter tags..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Receipt Modal */}
      <RequestReceiptModal
        open={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        onSubmit={handleRequestReceipt}
        users={users}
        isSubmitting={isUploading}
      />

      {/* Version Upload Modal */}
      <Dialog open={showVersionModal} onOpenChange={setShowVersionModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload New Version</DialogTitle>
            <DialogDescription>
              Replace &quot;{fileForVersion?.file_name}&quot; with a new version. The old version will be kept in history.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <FileUploadZone
              onUpload={async (file) => {
                if (fileForVersion) {
                  await handleUploadVersion(fileForVersion, file);
                }
              }}
              categories={[]}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowVersionModal(false);
              setFileForVersion(null);
            }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
